import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatCompact, formatVND } from '@/lib/utils';
import { computeMonthlyObligations, financialAlerts, envelopeAlerts } from '../../../finance';
import { useData } from '../../store/data';
import { useThisMonth } from '../../store/selectors';
import { useBudgetMonth } from '../budget/useBudget';
import { DayList, type Handlers } from '../planned/DayList';
import { ConfirmSheet, EditSheet, SkipSheet } from '../planned/Sheets';
import { plannedForMonth, type PlannedEntry } from '../planned/plannedEntries';
import { priorityActions } from './actions';

const dm = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Cảnh báo cần xử lý: đỏ sẫm chỉ dành cho việc phải làm, luôn kèm chữ và biểu tượng. */
export function Alerts() {
  const { sources, debts } = useData(s => s.data);
  const month = useThisMonth();
  const budget = useBudgetMonth(month.year, month.month);
  const alerts = useMemo(
    () => [
      ...financialAlerts({ sources, debts, monthIncome: month.income, monthExpense: month.expense }),
      ...envelopeAlerts(budget.status, month.month)
    ].sort((a, b) => (a.level === b.level ? 0 : a.level === 'bad' ? -1 : 1)),
    [sources, debts, month, budget.status]
  );
  if (alerts.length === 0) return null;
  return (
    <ul className="space-y-2" aria-label="Cảnh báo">
      {alerts.map(a => (
        <li key={a.id} className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', a.level === 'bad' ? 'border-bad/30 bg-bad-soft' : 'border-warn/30 bg-warn-soft')}>
          <AlertTriangle size={18} className={cn('mt-0.5 shrink-0', a.level === 'bad' ? 'text-bad' : 'text-warn')} aria-hidden />
          <div>
            <p className={cn('font-medium', a.level === 'bad' ? 'text-bad' : 'text-warn')}>{a.title}</p>
            <p className="text-sm text-muted-foreground">{a.detail}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

const SNOOZE_KEY = 'v2-actions-hidden';
const readHidden = (): string[] => { try { return JSON.parse(localStorage.getItem(SNOOZE_KEY) || '[]'); } catch { return []; } };

/** Việc ưu tiên có thể làm ngay trong app (nhập lãi suất, xoá khoản trùng, xem tổng kết…). "Ẩn" chỉ nhớ trên trình duyệt này. */
export function PriorityList() {
  const { sources, debts, expenses } = useData(s => s.data);
  const month = useThisMonth();
  const budget = useBudgetMonth(month.year, month.month);
  const [hidden, setHidden] = useState<string[]>(readHidden);
  const budgetsSet = Object.values(budget.limits ?? {}).some(v => Number(v) > 0);
  const items = useMemo(
    () => priorityActions({ now: new Date(), sources, debts, expenses, budgetsSet }).filter(a => !hidden.includes(a.id)),
    [sources, debts, expenses, budgetsSet, hidden]
  );
  if (items.length === 0) return null;
  const hide = (id: string) => { const next = [...hidden, id].slice(-60); setHidden(next); try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(next)); } catch { /* không lưu được thì chỉ ẩn trong phiên */ } };
  return (
    <section className="space-y-2" aria-label="Việc ưu tiên">
      <h2 className="px-1 text-base font-semibold">Việc ưu tiên <span className="num font-normal text-muted-foreground">{items.length}</span></h2>
      <ul className="space-y-2">
        {items.map(a => (
          <li key={a.id} className={cn('flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3', a.level === 'warn' ? 'border-warn/30 bg-warn-soft' : 'border-border bg-card')}>
            <div className="min-w-0 flex-1 basis-56"><p className="font-medium">{a.title}</p><p className="text-sm text-muted-foreground">{a.detail}</p></div>
            <div className="flex shrink-0 items-center gap-2">
              <Link to={a.to} className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">{a.cta} <ArrowUpRight size={14} aria-hidden /></Link>
              <button type="button" onClick={() => hide(a.id)} className="min-h-9 px-2 text-sm text-muted-foreground hover:text-foreground">Ẩn</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Việc cần làm: các khoản dự kiến đã quá hạn hoặc đến hạn trong 7 ngày tới, xác nhận ngay tại đây. */
export function DueList() {
  const d = useData(s => s.data);
  const [confirm, setConfirm] = useState<PlannedEntry | null>(null);
  const [edit, setEdit] = useState<PlannedEntry | null>(null);
  const [skip, setSkip] = useState<PlannedEntry | null>(null);
  const now = new Date();
  const items = useMemo(() => {
    const week = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7).getTime();
    return plannedForMonth({ plannedExpenses: d.plannedExpenses, plannedIncomes: d.plannedIncomes, plannedPayments: d.plannedPayments, recurringTemplates: d.recurringTemplates }, now.getFullYear(), now.getMonth() + 1, now)
      .filter(e => e.status === 'pending' && !e.virtual && (e.daysLate > 0 || (e.date && e.date.getTime() <= week)));
  }, [d.plannedExpenses, d.plannedIncomes, d.plannedPayments, d.recurringTemplates]); // eslint-disable-line react-hooks/exhaustive-deps
  const h: Handlers = { onConfirm: setConfirm, onEdit: setEdit, onSkip: setSkip };
  const shown = items.slice(0, 6);
  return (
    <section className="space-y-2" aria-label="Việc cần làm">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h2 className="text-base font-semibold">Việc cần làm <span className="num font-normal text-muted-foreground">{items.length}</span></h2>
        <Link to="/v2/du-kien" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">Mở Dự kiến <ArrowUpRight size={14} aria-hidden /></Link>
      </div>
      {items.length === 0 ? (
        <Card className="p-6 text-muted-foreground">Không có khoản nào quá hạn hoặc đến hạn trong 7 ngày tới.</Card>
      ) : (
        <>
          <DayList items={shown} numeral="text-brand" h={h} />
          {items.length > shown.length && <p className="px-1 text-sm text-muted-foreground">Còn {items.length - shown.length} khoản nữa ở mục Dự kiến.</p>}
        </>
      )}
      {confirm && <ConfirmSheet entry={confirm} onClose={() => setConfirm(null)} />}
      {edit && <EditSheet entry={edit} onClose={() => setEdit(null)} />}
      {skip && <SkipSheet entry={skip} onClose={() => setSkip(null)} />}
    </section>
  );
}

/**
 * Nghĩa vụ nợ & thẻ trong tháng: CHỈ tính các khoản có kế hoạch trong Dự kiến (khớp với mục Dự kiến).
 * Thẻ/khoản vay chưa có kế hoạch hay mẫu thì liệt kê riêng bên dưới, kèm ước tính tối thiểu (dấu "~") — không cộng vào tổng.
 */
export function ObligationsCard() {
  const { sources, debts, plannedPayments, creditPayments, recurringTemplates } = useData(s => s.data);
  const obligations = useMemo(() => computeMonthlyObligations(debts, sources, plannedPayments, creditPayments, new Date(), recurringTemplates), [debts, sources, plannedPayments, creditPayments, recurringTemplates]);
  const planned = obligations.items.filter(i => i.source === 'plan');
  const unplanned = obligations.items.filter(i => i.source !== 'plan');
  const planTotal = planned.reduce((s, i) => s + i.amount, 0);
  const row = (i: typeof planned[number]) => (
    <li key={i.key} className="flex items-baseline justify-between gap-3 py-2">
      <span className="min-w-0 truncate">{i.label}{i.dueDate && <span className="ml-2 text-sm text-muted-foreground">{dm(i.dueDate)}</span>}</span>
      <span className="num shrink-0 font-medium">{i.isEstimate && '~'}{formatCompact(i.amount)}</span>
    </li>
  );
  return (
    <Card className="min-w-0">
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base font-semibold"><CalendarClock size={16} aria-hidden /> Nợ và thẻ còn phải trả tháng này</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="font-slab num text-3xl font-bold">{formatVND(planTotal)}</p>
        {planned.length === 0 ? (
          <p className="text-muted-foreground">Không còn khoản nợ hay thẻ nào trong kế hoạch phải trả tháng này.</p>
        ) : <ul className="divide-y divide-border">{planned.map(row)}</ul>}
        {unplanned.length > 0 && (
          <div className="space-y-1 rounded-md bg-muted px-3 py-2">
            <p className="text-sm font-medium">Chưa có trong Dự kiến</p>
            <ul className="divide-y divide-border">{unplanned.map(row)}</ul>
            <p className="text-sm text-muted-foreground">Số tối thiểu ước tính (dấu ~), chưa cộng vào tổng. <Link to="/v2/du-kien" className="font-medium text-primary hover:underline">Thêm vào Dự kiến</Link> nếu thầy sẽ trả.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BudgetCard() {
  const month = useThisMonth();
  const budget = useBudgetMonth(month.year, month.month);
  const bt = budget.status.totals;
  const pct = bt.pct ?? 0;
  return (
    <Card className="min-w-0">
      <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Ngân sách tháng {month.month}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {bt.available > 0 ? (
          <>
            <p className="font-slab num text-3xl font-bold">{Math.round(pct)}%<span className="ml-2 font-sans text-base font-normal text-muted-foreground">đã dùng</span></p>
            <div className="h-2 overflow-hidden rounded-full bg-secondary" role="img" aria-label={`Đã dùng ${Math.round(pct)}% ngân sách`}>
              <div className={cn('h-full rounded-full transition-[width] duration-700 motion-reduce:transition-none', pct >= 100 ? 'bg-bad' : pct >= 80 ? 'bg-warn' : 'bg-good')} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <p className="num text-sm text-muted-foreground">
              Chi {formatCompact(bt.spent)}{bt.committed > 0 && ` cộng cam kết ${formatCompact(bt.committed)}`} trên {formatCompact(bt.available)}. {bt.remaining >= 0 ? `Còn ${formatVND(bt.remaining)}.` : `Vượt ${formatVND(-bt.remaining)}.`}
            </p>
            <Link to="/v2/ngan-sach" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">Chi tiết theo danh mục <ArrowUpRight size={14} aria-hidden /></Link>
          </>
        ) : (
          <>
            <p className="text-muted-foreground">Chưa đặt ngân sách cho tháng này. Đặt hạn mức cho vài danh mục chi lớn để theo dõi.</p>
            <Link to="/v2/ngan-sach" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">Đặt ngân sách theo danh mục <ArrowUpRight size={14} aria-hidden /></Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
